import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams, useMatch } from "react-router-dom";
import IGetCategoriesDTO from "../../../models/DTO/IGetCategoriesDTO";
import IGetCategoryCatalogItemsDTO from "../../../models/DTO/IGetCategoryCatalogItemsDTO";
import IOnGetCategoriesDTO from "../../../models/DTO/IOnGetCategoriesDTO";
import IOnGetCategoryCatalogItemsDTO from "../../../models/DTO/IOnGetCategoryCatalogItemsDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IUpdateOrderEntryDetailDTO from "../../../models/DTO/IUpdateOrderEntryDetailDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICatalog_Item from "../../../models/ICatalog_Item";
import ICategory from "../../../models/ICategory";
import ICategoryUI from "../../../models/ICategoryUI";
import IOrder from "../../../models/IOrder";
import IOrder_Item from "../../../models/IOrder_Item";
import OwLabel from "../../shared/OwLabel/OwLabel";
import "./EcommerceView.scss";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import EcommerceCartSidebar from "../EcommerceCartSidebar/EcommerceCartSidebar";
import { EcommerceCategorySidebar } from "../EcommerceCategorySidebar/EcommerceCategorySidebar";
import EcommerceListView from "../EcommerceListView/EcommerceListView";
import { EcommerceSearchSubHeader } from "../EcommerceSearchSubHeader/EcommerceSearchSubHeader";
import ISubmitOrderDTO from "../../../models/DTO/ISubmitOrderDTO";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import CategoryContext from "../../../contexts/CategoryContext";
import { ProcessOrderEntryItemFn } from "../../../models/OrderEntryTypes";
import UiContext from "../../../contexts/UiContext";
import { EcommerceCartOrderTotalsHeader } from "../EcommerceCartOrderTotalsHeader/EcommerceCartOrderTotalsHeader";
import { SocketContext } from "@contexts/SocketContext";
import IGetOrderDTO from "@models/DTO/IGetOrderDTO";
import { OrdersService } from "@components/orders/Orders/OrdersService";
import ViewAccessService from "@services/ViewAccessService";
import IUserTypeAccessDetail from "@models/IUserTypeAccessDetail";
import { OrderEntryApiService } from "@services/OrderEntryApiService";
import IOrder_ItemUI from "@models/IOrder_ItemUI";
import IOnCreateOrderEntryItemDTO from "@models/DTO/IOnCreateOrderEntryItemDTO";
import IOnDeleteOrderEntryItemDTO from "@models/DTO/IOnDeleteOrderEntryItemDTO";
import IOnUpdateOrderEntryItemDTO from "@models/DTO/IOnUpdateOrderEntryItemDTO";
import UserTypesEnum from "@models/UserTypesEnum";
import { hasEditAccess } from "@services/ScreenOptionsService";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";

type Props = {};

function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const EcommerceView = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const categoryContext = useContext(CategoryContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const orderEntryApiService = OrderEntryApiService();

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ search: string; category: string }>();
    const query = useQuery();
    const match = useMatch("/:entityType/:order_guid/ecom");

    const [searchItems, setSearchItems] = useState<ICatalog_Item[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [lookupWidth, setLookupWidth] = useState("0");
    const [lookupLeft, setLookupLeft] = useState("0");
    const [performedInitialSearch, setPerformedInitialSearch] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [getRootCategoriesActive, setGetRootCategoriesActive] = useState(false);
    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);
    const [topBarSet, setTopBarSet] = useState(false);
    const [categoryGuid, setCategoryGuid] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loadedCategories, setLoadedCategories] = useState(false);
    const [order, setOrder] = useState<IOrder | null>(null);

    const isReadOnly = () => {
        if (order) {
            const hasEditMode = hasEditAccess(ScreenOptionsEnum.ORDER_ENTRY, userModel, supplierModel, ViewAccessService.OrderEntry);

            return OrdersService.isReadOnly({
                order,
                isOfflineOrder: false,
                hasEditMode,
                supplierModel: supplierModel,
                userModel: userModel,
            });
        }

        return false;
    };

    const shouldHidePricingFn = useMemo(() => {
        return () => {
            return false;
        };
    }, []);

    const submitOrderFn = useCallback(
        (data: ISubmitOrderDTO) => {
            if (order) {
                orderEntryApiService.submitOrder(order, data);
            }
        },
        [order]
    );

    const addOrderEntryItem: ProcessOrderEntryItemFn = useCallback(
        (item, options) => {
            if (order) {
                uiContext.setActivityIndicator(true);
                orderEntryApiService.addOrderEntryItem(order, item, options);
            }
        },
        [order]
    );

    const deleteOrderEntryItem = useCallback(
        (item: IOrder_Item) => {
            if (order) {
                uiContext.setActivityIndicator(true);
                orderEntryApiService.deleteOrderEntryItem(order, item);
            }
        },
        [order]
    );

    const updateOrderEntryItem: ProcessOrderEntryItemFn = useCallback(
        (rowData, options) => {
            if (order) {
                uiContext.setActivityIndicator(true);
                orderEntryApiService.updateOrderEntryItem(order, rowData, options);
            }
        },
        [order]
    );

    const getCommonItems = () => {
        getTypeaheadCatalogItem(undefined, true, undefined, undefined, "ECV - getCommonItems", undefined);
    };

    const getTypeaheadCatalogItem = useCallback(
        (
            orderData?: IOrder,
            _getCommonItems?: boolean,
            _categoryGuid?: string,
            _searchTerm?: string,
            callingFn?: string,
            _categoryId?: number,
            _offset?: number
        ) => {
            const currentOrder = orderData || order;
            if (currentOrder?.Catalog) {
                const payload: ITypeaheadCatalogItemDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    catalogGuid: currentOrder.Catalog.guid,
                    name: _searchTerm || searchTerm || "",
                    limit,
                    offset: _offset || offset,
                    onlyActive: true,
                    includeTotal: true,
                    includeKitItems: true,
                    orderGuid: currentOrder.guid,
                    options: {
                        searchName: true,
                        searchDescription: true,
                        searchSku: true,
                        searchUpc: true,
                    },
                };

                if (_categoryGuid || categoryContext.category) {
                    // If we have a category ID directly, use it
                    if (_categoryId) {
                        payload.categoryId = _categoryId;
                    } else {
                        // console.log("getTypeaheadCatalogItem - _categoryGuid: ", _categoryGuid, rootCategories);
                        const categoryModel = rootCategories.find((cat) => cat.guid === _categoryGuid);

                        if (categoryModel) {
                            payload.categoryId = categoryModel.id;
                        } else if (_categoryGuid) {
                            // If not found in rootCategories but _categoryGuid is provided,
                            // it's likely a subcategory, so we can't determine the ID here
                            // The backend should handle this case
                        }

                        // console.log("getTypeaheadCatalogItem - categoryModel: ", categoryModel);

                        // Only check categoryContext.category if no _categoryGuid was provided
                        if (!_categoryGuid && categoryContext.category) {
                            payload.categoryId = categoryContext.category.id;
                        }
                    }

                    // console.log("getTypeaheadCatalogItem - categoryModel - post context: ", categoryModel);
                }

                if (!userModel.isSupplierUser) {
                    payload.vendorGuid = currentOrder.Vendor!.guid;
                }

                if (_getCommonItems) {
                    payload.onlyCommonItems = true;
                    payload.includeStandardIfNoCommonItems = true;
                }

                socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);

                setIsLoading(true);
                uiContext.setActivityIndicator(true);

                // if (callingFn) {
                //     console.log("getTypeaheadCatalogItem called from: ", callingFn);
                // }
            }
        },
        [order, rootCategories, categoryContext.category]
    );

    const onGetTypeaheadCatalogItem = useCallback((data: IOnTypeaheadCatalogItemDTO) => {
        setSearchItems(data.catalog_items);
        setPerformedInitialSearch(true);
        setTotalRecords(data.totalCatalogItems);
        setIsLoading(false);
        setCategoryGuid("");

        document.getElementById("mainView")?.scroll({ top: 0, left: 0, behavior: "auto" });
        uiContext.setActivityIndicator(false);
    }, []);

    const getProductsInCategory = (_categoryGuid: string) => {
        if (!order) return;

        const payload: IGetCategoryCatalogItemsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            categoryGuid: _categoryGuid,
            includeKitItems: true,
            catalogGuid: order.Catalog!.guid,
            priceGroupGuid: order.Price_Group!.guid,
            limit: 20,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CategoriesGetCategoryCatalogItems, payload);

        setIsLoading(true);
        setCategoryGuid(categoryGuid);
        uiContext.setActivityIndicator(isLoading);
    };

    const onGetProductsInCategory = useCallback(
        (data: IOnGetCategoryCatalogItemsDTO) => {
            setSearchItems(data.catalogItems);
            setIsLoading(false);
            uiContext.setActivityIndicator(isLoading);
        },
        [searchItems]
    );

    const getRootCategories = () => {
        const payload: IGetCategoriesDTO = {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            catalogGuid: order?.Catalog?.guid,
            onlyRootCategories: true,
        };

        socket.emit(EndpointsEnum.CategoriesGetCategories, payload);
        setGetRootCategoriesActive(true);

        socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
        socket.on(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
    };

    const onGetCategories = useCallback(
        (data: IOnGetCategoriesDTO) => {
            if (getRootCategoriesActive) {
                const _rootCategories = data.categories.map((category) => {
                    return {
                        ...category,
                        isOpen: false,
                    } as ICategoryUI;
                });

                if (supplierModel.has_new_releases_category) {
                    const newReleasesCategory = {
                        guid: "NEW_RELEASES",
                        id: 0,
                        isOpen: false,
                        name: "New Releases",
                        created_at: "",
                        updated_at: "",
                    } as ICategoryUI;

                    _rootCategories.unshift(newReleasesCategory);
                }

                setGetRootCategoriesActive(false);
                setLoadedCategories(true);
                setRootCategories(_rootCategories);
            }
        },
        [getRootCategoriesActive]
    );

    const getOrder = useCallback(() => {
        if (match?.params.order_guid) {
            const payload: IGetOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                orderGuid: match?.params.order_guid,
                supplierGuid: supplierModel.guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.OrderGetOrder, payload);

            uiContext.setActivityIndicator(true);
        }
    }, [match]);

    const onGetOrder = useCallback((data: IOnGetOrderDTO) => {
        setOrder(data.order);
        uiContext.setActivityIndicator(false);

        // set the window title
        document.title = `Orderwerks >> ${data.order.name}`;
    }, []);

    const onCreateOrderEntryItem = useCallback(
        (data: IOnCreateOrderEntryItemDTO) => {
            uiContext.setActivityIndicator(false);
            getOrder();
        },
        [getOrder]
    );

    const onDeleteOrderEntryItem = useCallback(
        (data: IOnDeleteOrderEntryItemDTO) => {
            uiContext.setActivityIndicator(false);
            getOrder();
        },
        [getOrder]
    );

    const onUpdateOrderEntryItem = useCallback(
        (data: IOnUpdateOrderEntryItemDTO) => {
            uiContext.setActivityIndicator(false);
            getOrder();
        },
        [getOrder]
    );

    const sockets: IUiSocketSetup[] = useMemo(
        () => [
            { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onGetTypeaheadCatalogItem },
            { endpoint: EndpointsEnum.CategoriesOnGetCategoryCatalogItems, fn: onGetProductsInCategory },
            { endpoint: EndpointsEnum.CategoriesOnGetCategories, fn: onGetCategories },
            { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
            { endpoint: EndpointsEnum.OrderEntryOnCreateOrderEntryItem, fn: onCreateOrderEntryItem },
            { endpoint: EndpointsEnum.OrderEntryOnDeleteOrderEntryItem, fn: onDeleteOrderEntryItem },
            { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryItem, fn: onUpdateOrderEntryItem },
        ],
        [
            onGetTypeaheadCatalogItem,
            onGetProductsInCategory,
            onGetCategories,
            onGetOrder,
            onCreateOrderEntryItem,
            onDeleteOrderEntryItem,
            onUpdateOrderEntryItem,
        ]
    );

    // const getCommonItems = useCallback(() => {
    //     getTypeaheadCatalogItem(undefined, true, undefined, undefined, "ECV - getCommonItems");
    // }, [getTypeaheadCatalogItem]);

    const performSearch = (_searchTerm: string, _categoryGuid?: string, callingFn?: string, _categoryId?: number) => {
        let _offset = offset;

        if (_searchTerm !== searchTerm) {
            _offset = 0;
        }

        const searchArr: string[] = [];

        if (_searchTerm.length) {
            searchArr.push(`search=${_searchTerm}`);
        }

        if (_categoryGuid) {
            searchArr.push(`category=${_categoryGuid}`);
        }

        const search = `?${searchArr.join("&")}`;

        setSearchTerm(_searchTerm);
        setOffset(_offset);

        navigate({
            pathname: `/${match?.params.entityType}/${order!.guid}/ecom`,
            search,
        });

        getTypeaheadCatalogItem(undefined, undefined, _categoryGuid, _searchTerm, callingFn || "ECV - performSearch", _categoryId);
    };

    const setupSockets = () => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }
    };

    const tearDownSockets = () => {
        for (const _socket of sockets) {
            socket.removeListener(_socket.endpoint, _socket.fn);
        }
    };

    useEffect(() => {
        uiContext.setIsCheckoutView(false);
        uiContext.setActivityIndicator(true);

        setupSockets();

        getOrder();

        return () => {
            tearDownSockets();

            uiContext.setSubHeaderComponent(null);

            uiContext.setRightSideComponent(null);
            uiContext.toggleRightSide(false);

            categoryContext.setFn(undefined);

            // Reset topBarSet so header can be set again when returning to this view
            setTopBarSet(false);
        };
    }, []);

    useEffect(() => {
        if (order && !loadedCategories) {
            getRootCategories();
        }

        if (order) {
            uiContext.setCartItemCount(order.Order_Items.length);
            setSidebarCartComponent();

            if (!performedInitialSearch) {
                getTypeaheadCatalogItem(order, true, undefined, undefined, "ECV - onGetOrder", undefined);
            }

            // Ensure sub header is set when returning to this view
            if (loadedCategories && !uiContext.subHeaderComponent) {
                setSubHeaderComponent();
            }
        }
    }, [order, loadedCategories, performedInitialSearch]);

    useEffect(() => {
        tearDownSockets();
        setupSockets();

        uiContext.setActivityIndicator(isLoading);

        if (!isLoading) {
            const search = params.search;
            const category = params.category;

            if (!search && category && categoryGuid !== category) {
                getProductsInCategory(category);
            } else if (search && searchTerm !== search) {
                if (category) {
                    performSearch(search, category, "ECV useEffect - isLoading category");
                } else {
                    performSearch(search, undefined, "ECV useEffect - isLoading else");
                }
            } else {
                // if (!uiContext.isCheckoutView && !performedInitialSearch) {
                //     getTypeaheadCatalogItem(undefined, true, undefined, undefined, "ECV useEffect - isLoading");
                // }
            }
        }
    }, [categoryGuid, searchTerm]);

    useEffect(() => {
        if (!topBarSet && loadedCategories && !uiContext.subHeaderComponent) {
            setSubHeaderComponent();

            setTopBarSet(true);
        }
    }, [topBarSet, loadedCategories, uiContext.subHeaderComponent]);

    // Force header update when returning to main view
    useEffect(() => {
        // When on main ecommerce view (not checkout), always ensure we have the right header
        if (loadedCategories && order && !location.pathname.includes("/checkout")) {
            // Always recreate the header when returning to main view
            const _isReadOnly = isReadOnly();

            const component = (
                <EcommerceSearchSubHeader
                    order={order}
                    isCheckoutView={false}
                    isReadOnly={_isReadOnly}
                    toggleCartSidebar={toggleCartSidebar}
                    closeCartSidebar={closeCartSidebar}
                    cartOpen={cartOpen}
                    performSearch={performSearch}
                    toggleCatalogSidebar={toggleCatalogSidebar}
                    lookupWidth={lookupWidth}
                    lookupLeft={lookupLeft}
                    getCommonItems={getCommonItems}
                    categories={rootCategories}
                    viewCart={toggleCheckoutMobileView}
                />
            );

            uiContext.setSubHeaderComponent(component);
            setTopBarSet(true);
        }
    }, [location.pathname, loadedCategories, order]);

    // useEffect(() => {
    //     if (categoryContext.category) {
    //         performSearch("", categoryContext.category.guid, "EV useEffect categoryContext");
    //     }
    // }, [categoryContext.category]);

    // This is for the pagination changing.
    useEffect(() => {
        let _categoryGuid: string | undefined;
        let _categoryId: number | undefined;

        if (query?.get("category")) {
            _categoryGuid = query.get("category") || undefined;
            // Find the category ID if we have a category GUID
            const categoryModel = rootCategories.find((cat) => cat.guid === _categoryGuid);
            if (categoryModel) {
                _categoryId = categoryModel.id;
            }
        }

        // Only get common items if we're not in a category and don't have a search term
        const shouldGetCommonItems = !_categoryGuid && !searchTerm;

        getTypeaheadCatalogItem(
            undefined,
            shouldGetCommonItems,
            _categoryGuid,
            searchTerm,
            "ECV - onPaginationChanged",
            _categoryId,
            offset
        );
    }, [offset, query, searchTerm, rootCategories]);

    const cancelSidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    // const toggleCartSidebar = () => {
    //     props.toggleRightSide(true);
    // };

    const toggleCategorySidebar = () => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);
    };

    const viewCategory = (category: ICategory) => {
        setTopBarSet(false);
        toggleCategorySidebar();

        performSearch("", category.guid, "ECV - viewCategory", category.id);
    };

    const closeCartSidebar = () => {
        uiContext.setRightSideComponent(null);
        uiContext.toggleRightSide(false);
        setCartOpen(false);
    };

    // const toggleCartSidebar = () => {
    //     props.toggleRightSide(true);
    // };

    const cartSidebarComponent = useMemo(() => {
        if (order) {
            const _isReadOnly = isReadOnly();
            return (
                <EcommerceCartSidebar
                    order={order}
                    cancelFn={cancelSidebar}
                    deleteOrderEntryItem={deleteOrderEntryItem}
                    updateOrderEntryItem={updateOrderEntryItem}
                    toggleCheckoutView={() => {
                        uiContext.setIsCheckoutView(!uiContext.isCheckoutView);
                        navigate({
                            pathname: `/${match?.params.entityType}/${order!.guid}/entry`,
                            search: `?checkout=${uiContext.isCheckoutView}&view=ecom&mobile=false`,
                        });
                        uiContext.toggleRightSide(false);
                    }}
                    isCheckoutView={uiContext.isCheckoutView}
                    shouldHidePricingFn={shouldHidePricingFn}
                    submitOrder={submitOrderFn}
                    isReadOnly={_isReadOnly}
                />
            );
        }
    }, [order]);

    // Update cart sidebar when order changes and cart is open
    useEffect(() => {
        if (cartOpen && cartSidebarComponent) {
            uiContext.setRightSideComponent(cartSidebarComponent);
        }
    }, [order, cartOpen, cartSidebarComponent]);

    const toggleCatalogSidebar = () => {
        if (order) {
            const component = (
                <EcommerceCategorySidebar
                    rootCategories={rootCategories}
                    toggleSidebar={toggleCategorySidebar}
                    orderModel={order}
                    viewCategory={viewCategory}
                />
            );

            uiContext.setSidebarComponent(component);
            uiContext.toggleSidebar(true, { pullRight: false });
        }
    };

    const addToCart: ProcessOrderEntryItemFn = (item, options) => {
        addOrderEntryItem(item, options);
    };

    const openDetailView = (_item: ICatalog_Item) => {};

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
    };

    const setSidebarCartComponent = () => {
        if (cartSidebarComponent) {
            uiContext.setRightSideComponent(cartSidebarComponent);
        }
    };

    const toggleCheckoutMobileView = useCallback(() => {
        uiContext.setIsCheckoutView(!uiContext.isCheckoutView);
        navigate({
            pathname: `/${match?.params.entityType}/${order!.guid}/ecom/checkout`,
        });
        uiContext.setSubHeaderComponent(null);
        uiContext.toggleRightSide(false);
    }, [uiContext, navigate, order]);

    const toggleCartSidebar = useCallback(() => {
        if (order) {
            setCartOpen(true);
            uiContext.setIsCheckoutView(false);
            setSidebarCartComponent();
            uiContext.toggleRightSide(true);
        }
    }, [order]);

    const setSubHeaderComponent = useCallback(() => {
        if (loadedCategories && order && !uiContext.subHeaderComponent) {
            const _isReadOnly = isReadOnly();

            const component = (
                <EcommerceSearchSubHeader
                    order={order}
                    isCheckoutView={false}
                    isReadOnly={_isReadOnly}
                    toggleCartSidebar={toggleCartSidebar}
                    closeCartSidebar={closeCartSidebar}
                    cartOpen={cartOpen}
                    performSearch={performSearch}
                    toggleCatalogSidebar={toggleCatalogSidebar}
                    lookupWidth={lookupWidth}
                    lookupLeft={lookupLeft}
                    getCommonItems={getCommonItems}
                    categories={rootCategories}
                    viewCart={toggleCheckoutMobileView}
                />
            );

            uiContext.setSubHeaderComponent(component);
        }
    }, [loadedCategories, order]);

    const headerComponent = useMemo(() => {
        if (order) {
            return (
                <EcommerceCartOrderTotalsHeader
                    order={order}
                    shouldHidePricingFn={shouldHidePricingFn}
                    submitOrder={submitOrderFn}
                    reviewAndSubmit={() => {
                        navigate({
                            pathname: `/${match?.params.entityType}/${order!.guid}/ecom/checkout`,
                        });
                        uiContext.toggleRightSide(false);
                    }}
                />
            );
        }
    }, [order, shouldHidePricingFn, submitOrderFn, navigate, match?.params.entityType]);

    useEffect(() => {
        if (order && headerComponent) {
            uiContext.setRightSideHeaderComponent(headerComponent);
        }
    }, [order, headerComponent]);

    return (
        <div className={"EcommerceView"}>
            <div className={"content-view container"}>
                <div className={"ecomm-grid"}>
                    {order &&
                        searchItems.map((item) => {
                            return (
                                <EcommerceListView
                                    key={item.guid}
                                    catalogItem={item}
                                    priceGroupName={order?.Price_Group?.value || ""}
                                    addToCart={addToCart}
                                    openDetailView={openDetailView}
                                    isReadOnly={isReadOnly()}
                                    order={order}
                                    socket={socket}
                                />
                            );
                        })}

                    {order && !searchItems.length && performedInitialSearch ? (
                        <div className={"no-results"}>
                            <OwLabel title={"no_results"} />
                        </div>
                    ) : null}
                </div>
                {order && searchItems.length && totalRecords ? (
                    <div className={"paginationBar flex-grow-1 flex-md-grow-0 justify-content-center"}>
                        <PaginationBar
                            offset={offset}
                            limit={limit}
                            totalRecords={totalRecords}
                            onPaginationChanged={onPaginationChanged}
                            hideLimit={true}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EcommerceView;
